import type {
  AppleAuthRequest,
  GoogleAuthRequest,
  GoogleAuthCodeRequest,
  SocialAuthResponse,
} from "@common/api-interface/auth";
import type { TrpcClient } from "@common/api-interface/trpc-client";
import type {
  GetUserPreferencesResponse,
  GetUserPremiumResponse,
  GetUserResponse,
  SetOnboardingSeenQuery,
  UpdateUserPreferencesRequest,
  UpdateUserPreferencesResponse,
} from "@common/api-interface/users";
import type { UserReferralInfo } from "@common/types/referral";
import type { UserWithExperience } from "@common/types/user";
import type { UserPreferences } from "@common/types/user-preferences";
import type { Http } from "@common/utils/http";

export const makeUserService = ({
  Http,
  trpc,
}: {
  Http: Http;
  trpc: TrpcClient;
}) => {
  return {
    deleteUser,
    getUser,
    getUserIsPremium,
    verifyGoogleToken,
    verifyGoogleAuthCode,
    verifyAppleToken,
    setOnboardingSeen,
    setWaitlist,
    getUserPreferences,
    updatePreferences,
    getUserReferralInfo,
  };

  async function verifyGoogleToken(
    id_token: string,
    referralCode: string | undefined,
    platform: "iOS" | "android" | "web"
  ): Promise<SocialAuthResponse | undefined> {
    let response = await Http.json<GoogleAuthRequest, SocialAuthResponse>(
      "POST",
      `/v1/auth/google`,
      {
        googleToken: id_token,
        platform,
        referralCode,
      }
    );

    if (response && response.token) {
      return response;
    }

    throw new Error("Malformed response");
  }

  async function verifyAppleToken(
    authorizationCode: string,
    fullName: string | undefined,
    referralCode: string | undefined,
    platform: "iOS" | "android" | "web"
  ): Promise<SocialAuthResponse | undefined> {
    let response = await Http.json<AppleAuthRequest, SocialAuthResponse>(
      "POST",
      `/v1/auth/apple`,
      {
        authorizationCode,
        platform,
        fullName,
        referralCode,
      }
    );

    if (response && response.token) {
      return response;
    }

    throw new Error("Malformed response");
  }

  async function setOnboardingSeen(userId: string) {
    await Http.json<{}, SetOnboardingSeenQuery>(
      "POST",
      `/v1/users/${userId}/experiences/onboarding`,
      {}
    );
  }

  async function setWaitlist(userId: string) {
    await Http.json<{}, SetOnboardingSeenQuery>(
      "POST",
      `/v1/users/${userId}/experiences/waitlist`,
      {}
    );
  }

  async function getUser(userId: string): Promise<UserWithExperience> {
    let response = await Http.json<{}, GetUserResponse>(
      "GET",
      `/v1/users/${userId}`,
      {}
    );

    if (response) {
      return response;
    }

    throw new Error("User not found");
  }

  async function getUserIsPremium(userId: string): Promise<boolean> {
    let response = await Http.json<{}, GetUserPremiumResponse>(
      "GET",
      `/v1/users/${userId}/premium`,
      {}
    );

    if (response) {
      return response.isPremium;
    }

    throw new Error("User not found");
  }

  async function getUserPreferences(userId: string) {
    let response = await Http.json<{}, GetUserPreferencesResponse>(
      "GET",
      `/v1/users/${userId}/preferences`,
      {}
    );

    return response;
  }

  async function updatePreferences(
    userId: string,
    preferences: Partial<Omit<UserPreferences, "userId">>
  ): Promise<boolean> {
    const response = await Http.json<
      UpdateUserPreferencesRequest,
      UpdateUserPreferencesResponse
    >("POST", `/v1/users/${userId}/preferences`, preferences);

    if (response) return response.success;

    throw new Error("Failed to update preferences");
  }

  async function getUserReferralInfo(): Promise<UserReferralInfo> {
    return await trpc.users.getUserReferralInfo.query();
  }

  async function verifyGoogleAuthCode(
    authCode: string,
    referralCode: string | undefined,
    platform: "web" // "iOS" | "android" |
  ): Promise<SocialAuthResponse | undefined> {
    let response = await Http.json<GoogleAuthCodeRequest, SocialAuthResponse>(
      "POST",
      `/v2/auth/google`,
      {
        authCode,
        platform,
        referralCode,
      }
    );

    if (response && response.token) {
      return response;
    }

    throw new Error("Malformed response");
  }

  async function deleteUser(confirmationEmail: string) {
    return await trpc.users.deleteUser.mutate({
      confirmationEmail,
    });
  }
};

export type UserService = ReturnType<typeof makeUserService>;
